import dayjs from 'dayjs'

import { DEFAULT_CONTRACT, DEFAULT_PAYMENT_APPLICATION, DEFAULT_PAYMENT_CERTIFICATE } from '@/helpers'
import i18n from '@/plugins/i18n'

export const PAYMENT_CERTIFICATE_ROW_TYPE = {
  OPTION: 'option',
  PRICE_ADJUSTMENT: 'price_adjustment'
}

export const DEDUCTION_TYPE = {
  DEDUCTION: 'deduction',
  ON_HOLD: 'onHold',
  RETENTION: 'retention'
}

export const DETAILS_FIELD_TYPE = {
  OPTION: 'optionNo',
  VARIATION: 'variationNo',
  STATEMENT_DATE: 'statementDate'
}

export const PAYMENT_CERTIFICATE_TABLE_COLOR = {
  BLUE: '#DCE6F1',
  GREY: '#BFBFBF',
  WHITE: '#FFFFFF'
}

export const getColumnClass = (index) => {
  return index === 1 ? 'column-width' : 'custom-text-width'
}

const getMilestoneNumbers = (paymentMilestones, hasVariationId) => {
  return paymentMilestones
    .filter((milestone) => milestone.name.length !== 0 && (hasVariationId ? milestone.variationId : !milestone.variationId))
    .map((milestone) => milestone.name)
    .join(', ')
}

export const initializeProjectDetailsData = (contract = DEFAULT_CONTRACT, payment = DEFAULT_PAYMENT_APPLICATION, paymentCertificate = DEFAULT_PAYMENT_CERTIFICATE) => {
  const currencies = contract.currencies.map((currency) => currency.key).join(', ')
  const milestoneNumbers = getMilestoneNumbers(payment.paymentMilestones, false)
  const variationsNumbers = getMilestoneNumbers(payment.paymentMilestones, true)
  const ipcNo = payment.paymentCertificate
  const purchaseOrderNumbers = contract.parts.map(({ purchaseOrder }) => purchaseOrder).join(', ')
  const statementDate = dayjs(paymentCertificate.statementDate).format('YYYY-MM-DD')

  return [
    { id: 1, content: i18n.t('payment.certificate.employer'), inputField: false, color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE },
    {
      id: 2,
      content: contract.employer.companyName,
      key: 'employer',
      inputField: false
    },
    {
      id: 3,
      content: i18n.t('payment.certificate.statementDate'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    { id: 4, content: statementDate, key: 'statementDate', inputField: true, type: 'date' },
    { id: 5, content: i18n.t('payment.certificate.contractor'), inputField: false, color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE },
    {
      id: 6,
      content: contract.contractor.companyName,
      key: 'contractor'
    },
    {
      id: 7,
      content: i18n.t('payment.certificate.applicationForIPC'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    { id: 8, content: ipcNo, key: 'ipcNo', inputField: false },
    {
      id: 9,
      content: i18n.t('payment.certificate.contractTitle'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    {
      id: 10,
      content: contract.contractName,
      key: 'contractTitle',
      inputField: false
    },
    {
      id: 11,
      content: i18n.t('payment.certificate.milestoneNo'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    {
      id: 12,
      content: milestoneNumbers,
      key: 'milestoneNo',
      inputField: false
    },
    {
      id: 13,
      content: i18n.t('payment.certificate.POnumber'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    {
      id: 14,
      content: purchaseOrderNumbers,
      key: 'referenceNo',
      inputField: false
    },
    {
      id: 17,
      content: i18n.t('common.currency'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    {
      id: 18,
      content: currencies,
      key: 'currency',
      inputField: false
    },
    {
      id: 19,
      content: i18n.t('payment.certificate.variationNo'),
      inputField: false,
      color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE
    },
    { id: 20, content: variationsNumbers, key: 'variationNo', inputField: false },
    { id: 21, content: '', key: '', inputField: false, color: PAYMENT_CERTIFICATE_TABLE_COLOR.BLUE },
    { id: 22, content: '', key: '', inputField: false }
  ]
}

export const initializeSumTableHeaders = () => {
  return [
    { text: i18n.t('common.name'), value: 'name' },
    { text: i18n.t('common.currency'), value: 'currency' },
    { text: i18n.t('common.value'), value: 'value' }
  ]
}

export const createSummaryInformationItems = ({ contractAmount = 0, contractCurrency = 'EUR', amendments = 0, adjustments = 0, options = 0, variations = 0, claims = 0, contractPrice = 0 }) => {
  return [
    {
      name: i18n.t('payment.certificate.signedContractPrice'),
      currency: contractCurrency,
      key: 'contractAmount',
      value: contractAmount
    },
    {
      name: i18n.t('payment.certificate.variationsAgreed'),
      currency: contractCurrency,
      key: 'variations',
      value: variations
    },
    {
      name: i18n.t('payment.certificate.claimsAgreed'),
      currency: contractCurrency,
      key: 'variations',
      value: claims
    },
    {
      name: i18n.t('payment.certificate.amendmentsAgreed'),
      currency: contractCurrency,
      key: 'amendments',
      value: amendments
    },
    {
      name: i18n.t('payment.certificate.adjustedContractPrice'),
      currency: contractCurrency,
      key: 'contractPrice',
      value: contractPrice
    }
  ]
}

export const initializeHeaders = (currency) => {
  return [
    { text: i18n.t('payment.certificate.ref'), value: 'ref', total: false },
    { text: i18n.t('payment.certificate.shortDescription'), value: 'description', total: false },
    { text: i18n.t('payment.certificate.percentOfContractAmount'), value: 'percent', total: true },
    { text: i18n.t('payment.certificate.total', { currency }), value: 'totalCents', total: true },
    {
      text: i18n.t('payment.certificate.cumulativeToDate', { currency }),
      value: 'cumulativeCents',
      total: true
    },
    {
      text: i18n.t('payment.certificate.cumulativePrevious', { currency }),
      value: 'cumulativePreviousCents',
      total: true
    },
    { text: i18n.t('payment.certificate.thisPayment', { currency }), value: 'paymentCents', total: true },
    { text: '', value: 'actions', sortable: false, total: false, width: '177px' }
  ]
}

const getPreviousPaymentApplications = (payment, paymentApplications) =>
  paymentApplications.filter(
    pa => pa.id !== payment.id && new Date(pa.createdAt) < new Date(payment.createdAt)
  )

const buildCumulativePreviousCentsMap = (previousApplications, includeVariations) =>
  previousApplications.reduce((cumulativeMap, pa) => {
    pa.paymentMilestones
      .filter(pm => includeVariations ? pm.variationId : !pm.variationId)
      .forEach(pm => {
        cumulativeMap[pm.id] = (cumulativeMap[pm.id] || 0) + (pm.partAmountCents || 0)
      })
    return cumulativeMap
  }, {})

const calculatePercentage = (amountCents, totalCents) =>
  `${((amountCents / totalCents) * 100).toFixed(2)} %`

const processMilestone = (
  pm,
  cumulativePreviousCentsMap,
  paymentApplication,
  contract,
  includePercentage = true,
  hasSubMilestones = false
) => {
  const cumulativePreviousCents = hasSubMilestones ? '' : cumulativePreviousCentsMap[pm.id] || 0
  const matchedMilestone = paymentApplication.paymentMilestones.find(({ id }) => id === pm.id)
  const currentPartAmountCents = matchedMilestone ? matchedMilestone.partAmountCents : 0

  const totalCents = hasSubMilestones ? '' : pm.paymentAmountCents.sum || pm.paymentAmountCents * pm.currency.value
  const percent = includePercentage
    ? pm.subMilestones?.length > 0
      ? 'N/A'
      : calculatePercentage(pm.paymentAmountCents.sum || pm.paymentAmountCents * pm.currency.value, contract.signedPriceCents)
    : 'N/A'

  return {
    ref: pm.name,
    description: pm.description,
    percent,
    totalCents,
    cumulativeCents: hasSubMilestones ? '' : currentPartAmountCents + cumulativePreviousCents,
    cumulativePreviousCents,
    paymentCents: hasSubMilestones ? '' : currentPartAmountCents,
    hasSubMilestones
  }
}

const processMilestones = (
  paymentMilestones,
  cumulativePreviousCentsMap,
  paymentApplication,
  contract,
  includeVariations,
  includePercentage
) =>
  paymentMilestones
    .filter(pm => includeVariations ? pm.variationId : !pm.variationId)
    .flatMap(pm =>
      pm.subMilestones?.length
        ? [
            processMilestone(pm, cumulativePreviousCentsMap, paymentApplication, contract, includePercentage, true),
            ...pm.subMilestones.map(subPm =>
              processMilestone(subPm, cumulativePreviousCentsMap, paymentApplication, contract, includePercentage)
            )
          ]
        : [processMilestone(pm, cumulativePreviousCentsMap, paymentApplication, contract, includePercentage)]
    )

export const initializeValueOfWorks = (
  payment = DEFAULT_PAYMENT_APPLICATION,
  paymentApplications,
  contract = DEFAULT_CONTRACT,
  paymentMilestones,
  amendments
) => {
  const duplicates = paymentMilestones.filter(milestone =>
    !amendments.some(amendment => amendment.no === milestone.name)
  )
  const previousPaymentApplications = getPreviousPaymentApplications(payment, paymentApplications)
  const cumulativePreviousCentsMap = buildCumulativePreviousCentsMap(previousPaymentApplications, false)
  const result = processMilestones(duplicates, cumulativePreviousCentsMap, payment, contract, false, true)

  return result.sort((a, b) => a.ref.localeCompare(b.ref))
}

export const initializeVariations = (
  payment = DEFAULT_PAYMENT_APPLICATION,
  paymentApplications,
  contract = DEFAULT_CONTRACT,
  paymentMilestones,
  claims,
  amendments
) => {
  const claimsVariations = claims.filter(claim => claim.status === 'FIN')

  const filteredMilestones = paymentMilestones.filter(milestone => milestone.variationId).filter(milestone =>
    !claimsVariations.some(claim => claim.id === milestone.variationId) &&
    !amendments.some(amendment => amendment.no === milestone.name)
  )

  const previousPaymentApplications = getPreviousPaymentApplications(payment, paymentApplications)
  const cumulativePreviousCentsMap = buildCumulativePreviousCentsMap(previousPaymentApplications, true)
  const result = processMilestones(filteredMilestones, cumulativePreviousCentsMap, payment, contract, true, false)

  return result.sort((a, b) => a.ref.localeCompare(b.ref))
}

export const initializeClaims = (
  payment = DEFAULT_PAYMENT_APPLICATION,
  paymentApplications,
  contract = DEFAULT_CONTRACT,
  paymentMilestones,
  claims
) => {
  const claimsVariations = claims.filter(claim => claim.status === 'FIN')

  const filteredMilestones = paymentMilestones.filter(milestone =>
    claimsVariations.some(claim => claim.id === milestone.variationId)
  )
  const previousPaymentApplications = getPreviousPaymentApplications(payment, paymentApplications)
  const cumulativePreviousCentsMap = buildCumulativePreviousCentsMap(previousPaymentApplications, true)
  const result = processMilestones(filteredMilestones, cumulativePreviousCentsMap, payment, contract, true, false)

  return result.sort((a, b) => a.ref.localeCompare(b.ref))
}

export const initializeAmendments = (
  payment = DEFAULT_PAYMENT_APPLICATION,
  paymentApplications,
  contract = DEFAULT_CONTRACT,
  paymentMilestones,
  amendments
) => {
  const filteredMilestones = paymentMilestones.filter(milestone =>
    amendments.some(amendment => amendment.no === milestone.name)
  )

  const previousPaymentApplications = getPreviousPaymentApplications(payment, paymentApplications)
  const cumulativePreviousCentsMap = buildCumulativePreviousCentsMap(previousPaymentApplications, false)
  const result = processMilestones(filteredMilestones, cumulativePreviousCentsMap, payment, contract, true, false)

  return result.sort((a, b) => a.ref.localeCompare(b.ref))
}

export const initializeDeductions = paymentCertificate => {
  const createDeductionItem = (type, description) => ({
    type,
    ref: '',
    description,
    percent: 0,
    totalCents: paymentCertificate[`${type}TotalCents`] || 0,
    cumulativeCents: paymentCertificate[`${type}CumulativeCents`] || 0,
    cumulativePreviousCents: paymentCertificate[`${type}CumulativePreviousCents`] || 0,
    paymentCents: paymentCertificate[`${type}PaymentCents`] || 0
  })

  return [
    createDeductionItem(DEDUCTION_TYPE.RETENTION, i18n.t('payment.certificate.retention')),
    createDeductionItem(DEDUCTION_TYPE.DEDUCTION, i18n.t('payment.certificate.deduction')),
    createDeductionItem(DEDUCTION_TYPE.ON_HOLD, i18n.t('payment.certificate.onHold'))
  ]
}
